import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  InputAdornment,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlatformPurchaseRecord, PlatformPurchasesService } from '../services/platformPurchasesService';

type SectionId = 'platform_purchases' | 'subscriptions';

const sectionItems: { id: SectionId; label: string; icon: React.ReactNode }[] = [
  { id: 'platform_purchases', label: 'Platform Purchases', icon: <ShoppingCartIcon fontSize="small" /> },
  { id: 'subscriptions', label: 'Subscriptions', icon: <CreditCardIcon fontSize="small" /> },
];

const formatMoney = (value: number, currency: string) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${Number(value || 0).toFixed(2)} ${String(currency || 'USD').toUpperCase()}`;
  }
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

const statusChip = (status: PlatformPurchaseRecord['status']) => {
  const s = String(status || '').toLowerCase();
  if (s === 'succeeded') return { label: 'Succeeded', color: 'success' as const };
  if (s === 'pending') return { label: 'Pending', color: 'warning' as const };
  if (s === 'failed') return { label: 'Failed', color: 'error' as const };
  if (s === 'cancelled') return { label: 'Cancelled', color: 'default' as const };
  if (s === 'refunded') return { label: 'Refunded', color: 'info' as const };
  return { label: status, color: 'default' as const };
};

const getProductPreviewImage = (p?: PlatformPurchaseRecord['product'] | null) => {
  const first = Array.isArray(p?.images) ? p?.images?.[0] : undefined;
  const url = String(first || '').trim();
  return url || undefined;
};

const cleanText = (v: unknown) => String(v || '').trim();

const SubscriptionsPlatformPurchasesPage: React.FC = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = React.useState<SectionId>('platform_purchases');
  const [query, setQuery] = React.useState('');
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [page, setPage] = React.useState<number>(1);

  const normalizedQuery = query.trim().toLowerCase();

  const { data: purchasesPage, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['platform_purchases', 'me', page, pageSize],
    queryFn: () => PlatformPurchasesService.listMyPurchasesPage({ page: page - 1, pageSize }),
    enabled: activeSection === 'platform_purchases',
  });

  const purchases = purchasesPage?.rows || [];
  const total = purchasesPage?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  React.useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const filteredPurchases = React.useMemo(() => {
    const list = Array.isArray(purchases) ? purchases : [];
    if (!normalizedQuery) return list;
    return list.filter((p) => {
      const itemsStr = Array.isArray(p.items)
        ? p.items.map((it) => `${it.title || ''} ${it.description || ''} ${it.badge || ''}`).join(' ')
        : '';
      const hay = `${p.id} ${p.provider || ''} ${p.provider_order_id || ''} ${p.status || ''} ${itemsStr}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [normalizedQuery, purchases]);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
        <Box
          sx={(t) => ({
            mb: { xs: 2, md: 3 },
            p: { xs: 2, md: 2.5 },
            borderRadius: 4,
            border: `1px solid ${t.palette.divider}`,
            backgroundColor: t.palette.background.paper,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          })}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'nowrap' }}>
              <Button
                onClick={() => navigate('/dashboard')}
                startIcon={<ArrowBackIcon />}
                sx={(t) => ({
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.85,
                  fontWeight: 900,
                  textTransform: 'none',
                  border: `1px solid ${t.palette.divider}`,
                  color: t.palette.text.primary,
                  backgroundColor: t.palette.background.paper,
                  '&:hover': {
                    backgroundColor: t.palette.action.hover,
                  },
                })}
              >
                Back
              </Button>
              <Typography variant={isMdDown ? 'h5' : 'h4'} sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                Subscriptions & Platform Purchases
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your subscriptions and review platform add-on purchases (Boost & Visibility).
            </Typography>
          </Box>

          <Chip
            icon={<ShoppingCartIcon />}
            label="Billing"
            sx={(t) => ({
              fontWeight: 900,
              borderRadius: 999,
              px: 0.75,
              backgroundColor: t.palette.action.hover,
              border: `1px solid ${t.palette.divider}`,
              alignSelf: { xs: 'flex-start', sm: 'center' },
            })}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          {!isMdDown && (
            <Card
              sx={(t) => ({
                width: { xs: '100%', md: 300 },
                position: { md: 'sticky' },
                top: { md: 18 },
                border: `1px solid ${t.palette.divider}`,
                boxShadow: 'none',
                overflow: 'hidden',
              })}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontWeight: 950 }}>Menu</Typography>
                </Box>
                <Divider />
                <Stack spacing={0}>
                  {sectionItems.map((it) => {
                    const active = it.id === activeSection;
                    return (
                      <Box
                        key={it.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveSection(it.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setActiveSection(it.id);
                          }
                        }}
                        sx={(t) => ({
                          px: 2,
                          py: 1.35,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: active ? t.palette.action.hover : 'transparent',
                          borderLeft: active ? `3px solid ${t.palette.primary.main}` : '3px solid transparent',
                          '&:hover': {
                            backgroundColor: t.palette.action.hover,
                          },
                        })}
                      >
                        {it.icon}
                        <Typography sx={{ fontWeight: active ? 900 : 700, color: 'text.primary' }}>{it.label}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isMdDown && (
              <Card
                sx={(t) => ({
                  border: `1px solid ${t.palette.divider}`,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  mb: 2,
                })}
              >
                <CardContent sx={{ p: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Section"
                    value={activeSection}
                    onChange={(e) => setActiveSection(e.target.value as SectionId)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
                  >
                    {sectionItems.map((it) => (
                      <MenuItem key={it.id} value={it.id}>
                        {it.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </CardContent>
              </Card>
            )}

            {activeSection === 'platform_purchases' && (
              <Card
                sx={(t) => ({
                  border: `1px solid ${t.palette.divider}`,
                  boxShadow: 'none',
                  overflow: 'hidden',
                })}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 950, mb: 0.25 }}>Platform Purchases</Typography>
                      <Typography variant="body2" color="text.secondary">
                        All your Boost & Visibility purchases.
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <TextField
                        select
                        size="small"
                        label="Per page"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(1);
                        }}
                        sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
                      >
                        {[5, 10, 15, 20].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Button
                        onClick={() => refetch()}
                        disabled={isFetching}
                        sx={(t) => ({
                          borderRadius: 999,
                          px: 2,
                          py: 0.9,
                          fontWeight: 900,
                          textTransform: 'none',
                          border: `1px solid ${t.palette.divider}`,
                          color: t.palette.text.primary,
                          backgroundColor: t.palette.background.paper,
                          '&:hover': {
                            backgroundColor: t.palette.action.hover,
                          },
                        })}
                      >
                        Refresh
                      </Button>
                    </Stack>
                  </Box>

                  <TextField
                    fullWidth
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search purchases (id, status, item name…)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
                  />

                  <Divider sx={{ my: 2.25 }} />

                  {isLoading && (
                    <Typography variant="body2" color="text.secondary">
                      Loading purchases…
                    </Typography>
                  )}

                  {!isLoading && error && (
                    <Typography variant="body2" color="error">
                      Failed to load purchases.
                    </Typography>
                  )}

                  {!isLoading && !error && filteredPurchases.length === 0 && (
                    <Box sx={{ py: 3 }}>
                      <Typography sx={{ fontWeight: 900, mb: 0.5 }}>No purchases yet</Typography>
                      <Typography variant="body2" color="text.secondary">
                        When you buy Boost & Visibility options, they will appear here.
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && !error && total > 0 && (
                    <Box
                      sx={(t) => ({
                        mb: 2,
                        mt: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                      })}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
                      </Typography>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        size={isMdDown ? 'small' : 'medium'}
                        shape="rounded"
                      />
                    </Box>
                  )}

                  <Stack spacing={1.5}>
                    {filteredPurchases.map((p) => {
                      const status = statusChip(p.status);
                      const items = Array.isArray(p.items) ? p.items : [];
                      const product = p.product || null;
                      const productImage = getProductPreviewImage(product);
                      const productTitle = cleanText(product?.name);
                      const hasProduct = Boolean(product && productTitle);
                      return (
                        <Card
                          key={p.id}
                          sx={(t) => ({
                            border: `1px solid ${t.palette.divider}`,
                            boxShadow: '0 14px 40px rgba(15, 23, 42, 0.10)',
                            overflow: 'hidden',
                            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.14)',
                            },
                          })}
                        >
                          <CardContent sx={{ p: { xs: 2, sm: 2.25 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 950, lineHeight: 1.15 }}>
                                  {formatMoney(Number(p.total_amount || 0), p.currency)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {formatDateTime(p.created_at)}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Chip size="small" label={status.label} color={status.color} />
                                <Chip size="small" variant="outlined" label={(p.provider || 'paypal').toUpperCase()} />
                              </Stack>
                            </Box>

                            {hasProduct && (
                              <Box
                                sx={(t) => ({
                                  mt: 1.75,
                                  p: 1.5,
                                  borderRadius: 3,
                                  border: `1px solid ${t.palette.divider}`,
                                  backgroundColor: t.palette.action.hover,
                                  display: 'flex',
                                  gap: 1.5,
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  flexWrap: 'wrap',
                                })}
                              >
                                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0, flex: 1 }}>
                                  <Avatar
                                    variant="rounded"
                                    src={productImage}
                                    sx={(t) => ({
                                      width: 56,
                                      height: 56,
                                      borderRadius: 3,
                                      border: `1px solid ${t.palette.divider}`,
                                      backgroundColor: t.palette.background.paper,
                                      fontWeight: 900,
                                    })}
                                  >
                                    {productTitle?.slice(0, 1)?.toUpperCase() || 'P'}
                                  </Avatar>

                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 950, lineHeight: 1.15 }} noWrap>
                                      {productTitle}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                      {formatMoney(Number(product?.price || 0), p.currency)}
                                      {product?.is_job ? ' • Job listing' : ' • Product listing'}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Button
                                  onClick={() => navigate(`/product/${product?.id}`)}
                                  endIcon={<OpenInNewIcon />}
                                  sx={(t) => ({
                                    borderRadius: 999,
                                    px: 1.75,
                                    py: 0.75,
                                    fontWeight: 900,
                                    textTransform: 'none',
                                    border: `1px solid ${t.palette.divider}`,
                                    color: t.palette.text.primary,
                                    backgroundColor: t.palette.background.paper,
                                    '&:hover': {
                                      backgroundColor: t.palette.action.hover,
                                    },
                                  })}
                                >
                                  View
                                </Button>
                              </Box>
                            )}

                            <Box sx={{ mt: 1.25 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                Purchase ID
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {p.id}
                              </Typography>
                            </Box>

                            {items.length > 0 && (
                              <Box sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                  Items
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 0.75 }}>
                                  {items.map((it) => {
                                    const title = cleanText(it.title || it.badge || it.id);
                                    const desc = cleanText(it.description);
                                    const price = typeof it.price === 'number' ? it.price : undefined;
                                    const itemCurrency = cleanText(it.currency || p.currency);
                                    return (
                                      <Box
                                        key={it.id}
                                        sx={(t) => ({
                                          p: 1.25,
                                          borderRadius: 3,
                                          border: `1px solid ${t.palette.divider}`,
                                          backgroundColor: t.palette.background.paper,
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          justifyContent: 'space-between',
                                          gap: 1.25,
                                          flexWrap: 'wrap',
                                        })}
                                      >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                          <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                                            {title}
                                          </Typography>
                                          {desc && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                              {desc}
                                            </Typography>
                                          )}
                                        </Box>

                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                          {typeof price === 'number' && (
                                            <Chip
                                              size="small"
                                              label={formatMoney(price, itemCurrency)}
                                              sx={{ borderRadius: 999, fontWeight: 900 }}
                                            />
                                          )}
                                          {it.recommended && (
                                            <Chip
                                              size="small"
                                              color="success"
                                              variant="outlined"
                                              label="Recommended"
                                              sx={{ borderRadius: 999, fontWeight: 900 }}
                                            />
                                          )}
                                        </Stack>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {activeSection === 'subscriptions' && (
              <Card
                sx={(t) => ({
                  border: `1px solid ${t.palette.divider}`,
                  boxShadow: 'none',
                  overflow: 'hidden',
                })}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Typography sx={{ fontWeight: 950, mb: 0.25 }}>Subscriptions</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Subscription management will appear here.
                  </Typography>

                  <Box
                    sx={(t) => ({
                      p: 2.25,
                      borderRadius: 3,
                      border: `1px dashed ${t.palette.divider}`,
                      backgroundColor: t.palette.action.hover,
                    })}
                  >
                    <Typography sx={{ fontWeight: 900 }}>Coming soon</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      This section is ready for your subscription plans and invoices.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SubscriptionsPlatformPurchasesPage;
